import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { buildLoginUrl } from '@/lib/redirect';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle, XCircle, LogIn } from 'lucide-react';
import { toast } from 'sonner';

type AcceptState = 'loading' | 'requires_auth' | 'success' | 'error';

interface AcceptResult {
  producer_id: string;
  role: string;
}

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isLoading: authLoading, refreshRoles } = useAuth();
  
  const [state, setState] = useState<AcceptState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [result, setResult] = useState<AcceptResult | null>(null);
  
  // Token from URL params, falling back to sessionStorage (preserved across login redirect)
  const urlToken = searchParams.get('token');
  const token = urlToken || sessionStorage.getItem('pendingInviteToken');

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMessage('Ingen inbjudningstoken hittades. Kontrollera att länken är korrekt.');
      return;
    }

    if (authLoading) return;

    if (!user) {
      setState('requires_auth');
      return;
    }

    // User is authenticated, try to accept the invite
    acceptInvite();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, authLoading]);

  const acceptInvite = async () => {
    if (!token) return;

    setState('loading');
    try {
      const { data, error } = await supabase.rpc('accept_producer_invite', {
        p_token: token,
      });

      if (error) {
        console.error('Error accepting invite:', error);
        if (error.message.includes('expired')) {
          setErrorMessage('Inbjudan har gått ut. Be om en ny inbjudan.');
        } else if (error.message.includes('already a member')) {
          setErrorMessage('Du är redan medlem i denna gård.');
        } else if (error.message.includes('Invalid')) {
          setErrorMessage('Ogiltig inbjudan. Kontrollera att länken är korrekt.');
        } else if (error.message.includes('team_limit_reached')) {
          // Parse the limit from error message: "team_limit_reached: max X members allowed (current: Y)"
          const match = error.message.match(/max\s*(\d+)\s*members.*current:\s*(\d+)/i);
          if (match) {
            setErrorMessage(`Teamet är fullt (${match[2]}/${match[1]} accepterade medlemmar). Kontakta gårdsägaren.`);
          } else {
            setErrorMessage('Teamet är fullt. Kontakta gårdsägaren för att frigöra platser.');
          }
        } else {
          setErrorMessage(error.message || 'Kunde inte acceptera inbjudan.');
        }
        setState('error');
        return;
      }

      const resultData = data as unknown as AcceptResult;
      setResult(resultData);
      setState('success');
      
      // CRITICAL FIX: Refresh roles so isProducer becomes true
      await refreshRoles();

      // Clear stored token after successful accept
      sessionStorage.removeItem('pendingInviteToken');
      
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['my-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['my-producers'] });
      queryClient.invalidateQueries({ queryKey: ['producer-members'] });
      queryClient.invalidateQueries({ queryKey: ['producer'] });

      // Best-effort: notify admin via edge function
      if (resultData.producer_id) {
        // We need the invitation_id — fetch it from producer_invitations by token hash
        // Since the RPC already consumed it, we look for the accepted invitation for this producer + user
        supabase.functions.invoke('notify-invite-accepted', {
          body: { invitation_id: null, producer_id: resultData.producer_id },
        }).catch(() => { /* best-effort */ });
      }
      
      toast.success('Du har gått med i teamet!');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ett oväntat fel uppstod';
      setErrorMessage(message);
      setState('error');
    }
  };

  const handleLoginRedirect = () => {
    // Store the invite token to use after login (fallback)
    sessionStorage.setItem('pendingInviteToken', token || '');
    // Include token in redirect URL so it's preserved across login
    const returnPath = `/accept-invite?token=${encodeURIComponent(token || '')}`;
    navigate(buildLoginUrl(returnPath));
  };

  const handleGoToFarm = async () => {
    if (result?.producer_id && user) {
      // Check if this member has already seen the welcome flow
      const { data: member } = await supabase
        .from('producer_members')
        .select('onboarded_at')
        .eq('producer_id', result.producer_id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (!member?.onboarded_at) {
        navigate(`/app/producer/farm/${result.producer_id}/welcome`);
      } else {
        navigate(`/app/producer/farm/${result.producer_id}`);
      }
    } else {
      navigate('/app/producer');
    }
  };

  if (state === 'loading' || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'requires_auth') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 rounded-full bg-primary/10 p-3 w-fit">
              <LogIn className="h-8 w-8 text-primary" />
            </div>
            <CardTitle>Logga in för att fortsätta</CardTitle>
            <CardDescription>
              Du behöver logga in för att acceptera inbjudan och gå med i teamet.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Button onClick={handleLoginRedirect} className="w-full">
              Logga in
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Har du inget konto? Du kan skapa ett när du loggar in.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 rounded-full bg-green-100 p-3 w-fit">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle>Välkommen till teamet!</CardTitle>
            <CardDescription>
              Du har nu gått med som {result?.role === 'admin' ? 'admin' : result?.role === 'staff' ? 'personal' : 'medlem'} i gården.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Button onClick={handleGoToFarm} className="w-full">
              Gå till gården
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  const isExpired = errorMessage.includes('gått ut');

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 rounded-full bg-destructive/10 p-3 w-fit">
            <XCircle className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle>Kunde inte acceptera inbjudan</CardTitle>
          <CardDescription>
            {isExpired
              ? 'Inbjudan har gått ut. Be den som bjöd in dig att skicka en ny inbjudan från värdteamets inställningar.'
              : errorMessage}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Button variant="outline" onClick={() => navigate('/')}>
            Gå till startsidan
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
